import { Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { CreateDtcDto } from '../../../../../shared/models/dtc/create-dtc-dto';
import { DtcListValue } from '../../../../../shared/models/dtc/dtc-list-value';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { errorToString } from '../../../../common/common-utils';
import { EntityType } from '../../../../../shared/enums/entity-type';
import { PropagateCreatedEntityModal } from '../../../modals/propagation-modal/propagate-created-entity-modal';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-new-dtc',
  imports: [
    FormsModule,
    CommonModule,
    TextAreaComponent,
    ReactiveFormsModule,
    FloppyIconComponent,
    LabelWithHelp,
    PropagateCreatedEntityModal,
    AgentActionDirective,
  ],
  templateUrl: './new-dtc.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './new-dtc.component.scss',
})
export class NewDtcComponent implements OnInit, OnDestroy {
  @ViewChild('propagateCreatedEntityModal') public propagateCreatedEntityModal!: PropagateCreatedEntityModal;
  public readonly tooltipDisabledText = 'Implementation Hint, Priority and Group is required';
  public swPartNumber = '';

  public dtcValues: DtcListValue[] = [];
  public newDtc: CreateDtcDto = {} as CreateDtcDto;
  public form!: FormGroup;
  public priority!: FormControl;
  public group!: FormControl;
  public implementationHint!: FormControl;
  public detailsOnTerminal!: FormControl;
  public remedyIntern!: FormControl;
  public remedyDealer!: FormControl;
  public remedyFarmer!: FormControl;
  public causeIntern!: FormControl;
  public causeDealer!: FormControl;
  public causeFarmer!: FormControl;
  public internalNotes!: FormControl;
  public tooltipText = this.tooltipDisabledText;

  private dtcApi = inject(DtcApiService);
  private toast = inject(HotToastService);
  private swPartNumberService = inject(SwPartNumberService);
  private formSubscription?: Subscription;

  public ngOnInit(): void {
    this.buildForm();
    this.unsubscribeForm();
    this.formSubscription = this.form.statusChanges.subscribe((status) => {
      this.tooltipText = status == 'VALID' ? 'Create new DTC' : this.tooltipDisabledText;
    });
    this.swPartNumber = this.swPartNumberService.getSwPartNumber()?.displayId ?? '';
  }

  public ngOnDestroy(): void {
    this.unsubscribeForm();
  }

  public createDtc(): void {
    this.prepareCreateDtc();
    this.dtcApi.createDtc(this.newDtc).subscribe({
      next: (dtcValue) => {
        this.dtcValues.push(dtcValue);
        this.toast.success(`Created successfully DTC ${dtcValue.dtc}`);
        this.form.reset();
        const swPartNumberId = this.swPartNumberService.getSwPartNumberId();
        if (swPartNumberId) {
          this.propagateCreatedEntityModal.openModal(swPartNumberId, EntityType.Dtc, dtcValue.id);
        }
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private prepareCreateDtc(): void {
    this.newDtc.priority = this.form.get('priority')?.value;
    this.newDtc.group = 255;
    this.newDtc.implementationHint = this.form.get('implementationHint')?.value;
    this.newDtc.detailsOnTerminal = this.form.get('detailsOnTerminal')?.value;
    this.newDtc.causeIntern = this.form.get('causeIntern')?.value;
    this.newDtc.remedyIntern = this.form.get('remedyIntern')?.value;
    this.newDtc.causeDealer = this.form.get('causeDealer')?.value;
    this.newDtc.remedyDealer = this.form.get('remedyDealer')?.value;
    this.newDtc.causeFarmer = this.form.get('causeFarmer')?.value;
    this.newDtc.remedyFarmer = this.form.get('remedyFarmer')?.value;
    this.newDtc.internalNotes = this.internalNotes.value === '' ? null : this.internalNotes.value;
  }

  private buildForm(): void {
    this.priority = new FormControl('', [Validators.required, Validators.min(0), Validators.max(255)]);
    this.group = new FormControl('', [Validators.required, Validators.min(255), Validators.max(255)]);
    this.group.disable();
    this.implementationHint = new FormControl(null, []);
    this.detailsOnTerminal = new FormControl(null, []);
    this.causeIntern = new FormControl(null, []);
    this.remedyIntern = new FormControl(null, []);
    this.causeDealer = new FormControl(null, []);
    this.remedyDealer = new FormControl(null, []);
    this.causeFarmer = new FormControl(null, []);
    this.remedyFarmer = new FormControl(null, []);
    this.internalNotes = new FormControl(null, []);

    this.form = new FormGroup({
      priority: this.priority,
      group: this.group,
      implementationHint: this.implementationHint,
      detailsOnTerminal: this.detailsOnTerminal,
      causeIntern: this.causeIntern,
      remedyIntern: this.remedyIntern,
      causeDealer: this.causeDealer,
      remedyDealer: this.remedyDealer,
      causeFarmer: this.causeFarmer,
      remedyFarmer: this.remedyFarmer,
      internalNotes: this.internalNotes,
    });
  }

  private unsubscribeForm(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
